import React, { Component } from "react";
import { computed } from "mobx";
import { observer } from "mobx-react";
import { withTranslation } from "react-i18next";
import { Button, Card, Checkbox, Form, Grid, Header, Icon, Input, Message, Select, Table } from "semantic-ui-react";
import PropTypes from "prop-types";
import FormStore from "~/stores/FormStore";
import HoursOfDayOptions from "~/constants/HoursOfDayOptions";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";

@observer
class UiMeet3dNotificationForm extends Component {
  @computed
  get notificationHourRanges() {
    const { formStore, decodeNotificationHourRanges } = this.props;
    const { model } = formStore;
    const { notificationHourRanges: encodedNotificationHourRanges } = model;

    const notificationHourRanges = [];

    for (let i = 0; i < encodedNotificationHourRanges.length; i += 1) {
      notificationHourRanges.push(decodeNotificationHourRanges(encodedNotificationHourRanges[i], "string"));
    }

    return notificationHourRanges;
  }

  getValidationError(key) {
    const { validationErrors } = this.props;

    if (typeof validationErrors[key] !== "undefined") {
      return validationErrors[key];
    }

    return null;
  }

  changeFormNotifications(day, value) {
    const { formStore } = this.props;
    const { model, setProperty } = formStore;
    const { notifications, notificationHourRanges } = model;

    notifications[day] = value;

    if (value) {
      notificationHourRanges[day] = "9-17";
    } else {
      notificationHourRanges[day] = "";
    }

    setProperty("notifications", notifications);
    setProperty("notificationHourRanges", notificationHourRanges);
  }

  changeFormNotificationHourRanges(day, index, fromOrTo, value) {
    const { formStore, encodeNotificationHourRanges, decodeNotificationHourRanges } = this.props;
    const { model, setProperty } = formStore;
    const { notificationHourRanges: encodedNotificationHourRanges } = model;

    const notificationHourRanges = decodeNotificationHourRanges(encodedNotificationHourRanges[day]);

    notificationHourRanges[index][fromOrTo] = parseInt(value, 10);

    encodedNotificationHourRanges[day] = encodeNotificationHourRanges(notificationHourRanges);

    setProperty("notificationHourRanges", encodedNotificationHourRanges);
  }

  addFormNotificationHourRange(day) {
    const { formStore, decodeNotificationHourRanges } = this.props;
    const { model, setProperty } = formStore;
    const { notificationHourRanges: encodedNotificationHourRanges } = model;

    const notificationHourRanges = decodeNotificationHourRanges(encodedNotificationHourRanges[day]);

    const hours = [];

    for (let i = 0; i < notificationHourRanges.length; i += 1) {
      for (let j = notificationHourRanges[i].from; j <= notificationHourRanges[i].to; j += 1) {
        hours.push(j);
      }
    }

    const maxHour = Math.max(...hours);

    // Default from/to
    let fromHour = 9;
    let toHour = 17;

    if (maxHour <= 21) {
      fromHour = maxHour + 1;
      toHour = fromHour + 1;
    }

    encodedNotificationHourRanges[day] += `,${fromHour.toString()}-${toHour.toString()}`;

    setProperty("notificationHourRanges", encodedNotificationHourRanges);
  }

  removeFormNotificationHourRange(day, index) {
    const { formStore, encodeNotificationHourRanges, decodeNotificationHourRanges } = this.props;
    const { model, setProperty } = formStore;
    const { notificationHourRanges: encodedNotificationHourRanges } = model;

    const notificationHourRanges = decodeNotificationHourRanges(encodedNotificationHourRanges[day]);

    notificationHourRanges.splice(index, 1);

    encodedNotificationHourRanges[day] = encodeNotificationHourRanges(notificationHourRanges);

    setProperty("notificationHourRanges", encodedNotificationHourRanges);
  }

  render() {
    const { defaultTimezone, formStore, t, validationErrors, contactPhone, email } = this.props;
    const { setProperty, getAggregatedError, model, onBlur } = formStore;
    const {
      notificationFirstTimeMovementEmail,
      notificationFirstTimeMovementContactPhone,
      notificationFirstTimeMovementWebhook,
      notificationLeadGenerationEmail,
      notificationLeadGenerationContactPhone,
      notificationLeadGenerationWebhook,
      notificationMeetingRequestEmail,
      notificationMeetingRequestContactPhone,
      notificationMeetingRequestWebhook,
      notifications,
      contactInformationUseDefault,
      contactInformationEmail,
      contactInformationContactPhone,
      contactInformationWebhook,
    } = model;

    const validationRules = {};
    const validation = getValidationErrors(validationRules, model);

    return (
      <Form style={{ marginTop: "1rem" }}>
        <h4>{t("clientPanelMisc:notificationEvents")}</h4>
        <Table definition>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>{t("email")}</Table.HeaderCell>
              <Table.HeaderCell>{t("sms")}</Table.HeaderCell>
              <Table.HeaderCell>{t("clientPanelMisc:webhook")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <Header as="h4">
                  <Header.Content>
                    {t("clientPanelMisc:firstTimeMovement")}
                    <Header.Subheader>
                      {t("clientPanelMisc:enableFirstTimeMovementNotificationsMessage")}
                    </Header.Subheader>
                  </Header.Content>
                </Header>
              </Table.Cell>
              <Table.Cell>
                <Form.Checkbox
                  checked={notificationFirstTimeMovementEmail}
                  onChange={(event, data) => setProperty("notificationFirstTimeMovementEmail", data.checked)}
                />
              </Table.Cell>
              <Table.Cell>
                <Form.Checkbox
                  checked={notificationFirstTimeMovementContactPhone}
                  onChange={(event, data) => setProperty("notificationFirstTimeMovementContactPhone", data.checked)}
                />
              </Table.Cell>
              <Table.Cell>
                <Form.Checkbox
                  checked={notificationFirstTimeMovementWebhook}
                  onChange={(event, data) => setProperty("notificationFirstTimeMovementWebhook", data.checked)}
                />
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <Header as="h4">
                  <Header.Content>
                    {t("clientPanelMisc:leadGeneration")}
                    <Header.Subheader>
                      {t("clientPanelMisc:enableLeadGenerationNotificationsMessage")}{" "}
                      <em>{t("clientPanelMisc:greetingBotNeedsToBeEnabled")}</em>
                    </Header.Subheader>
                  </Header.Content>
                </Header>
              </Table.Cell>
              <Table.Cell>
                <Form.Checkbox
                  checked={notificationLeadGenerationEmail}
                  onChange={(event, data) => setProperty("notificationLeadGenerationEmail", data.checked)}
                />
              </Table.Cell>
              <Table.Cell>
                <Form.Checkbox
                  checked={notificationLeadGenerationContactPhone}
                  onChange={(event, data) => setProperty("notificationLeadGenerationContactPhone", data.checked)}
                />
              </Table.Cell>
              <Table.Cell>
                <Form.Checkbox
                  checked={notificationLeadGenerationWebhook}
                  onChange={(event, data) => setProperty("notificationLeadGenerationWebhook", data.checked)}
                />
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <Header as="h4">
                  <Header.Content>
                    {t("clientPanelMisc:meetingRequests")}
                    <Header.Subheader>{t("clientPanelMisc:enableLeadMeetingRequestsMessage")}</Header.Subheader>
                  </Header.Content>
                </Header>
              </Table.Cell>
              <Table.Cell>
                <Form.Checkbox
                  checked={notificationMeetingRequestEmail}
                  onChange={(event, data) => setProperty("notificationMeetingRequestEmail", data.checked)}
                />
              </Table.Cell>
              <Table.Cell>
                <Form.Checkbox
                  checked={notificationMeetingRequestContactPhone}
                  onChange={(event, data) => setProperty("notificationMeetingRequestContactPhone", data.checked)}
                />
              </Table.Cell>
              <Table.Cell>
                <Form.Checkbox
                  checked={notificationMeetingRequestWebhook}
                  onChange={(event, data) => setProperty("notificationMeetingRequestWebhook", data.checked)}
                />
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
        <h4>{t("clientPanelMisc:notificationHours")}</h4>
        <p>{t("clientPanelMisc:yourDefaultTimezoneIs", { defaultTimezone: defaultTimezone.replaceAll("_", " ") })}</p>
        {notifications.map((notification, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Card key={index} fluid style={{ minHeight: "5rem" }}>
            <Card.Content>
              <Grid verticalAlign="middle">
                <Grid.Row>
                  <Grid.Column width="3">
                    <strong>{t(`day.${index}`)}</strong>
                  </Grid.Column>
                  <Grid.Column width="3">
                    <Checkbox
                      toggle
                      checked={notification}
                      onChange={() => this.changeFormNotifications(index, !notification)}
                    />
                  </Grid.Column>
                  <Grid.Column width="10">
                    {this.getValidationError(`uiMeet3dSettings.notificationHours.${index}.hourRanges`) && (
                      <Message
                        error
                        visible
                        content={this.getValidationError(`uiMeet3dSettings.notificationHours.${index}.hourRanges`)}
                      />
                    )}
                    <Grid columns="3">
                      {this.notificationHourRanges[index] &&
                        this.notificationHourRanges[index].map((notificationHourRange, index2) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Grid.Row key={index2}>
                            <Grid.Column>
                              <Form.Field
                                control={Select}
                                value={notificationHourRange.from}
                                options={HoursOfDayOptions}
                                onChange={(_event, { value }) =>
                                  this.changeFormNotificationHourRanges(index, index2, "from", value)
                                }
                                error={this.getValidationError(
                                  `uiMeet3dSettings.notificationHours.${index}.hourRanges.${index2}.from`,
                                )}
                              />
                            </Grid.Column>
                            <Grid.Column>
                              <Form.Field
                                control={Select}
                                value={notificationHourRange.to}
                                options={HoursOfDayOptions}
                                onChange={(_event, { value }) =>
                                  this.changeFormNotificationHourRanges(index, index2, "to", value)
                                }
                                error={this.getValidationError(
                                  `uiMeet3dSettings.notificationHours.${index}.hourRanges.${index2}.to`,
                                )}
                              />
                            </Grid.Column>
                            <Grid.Column>
                              <Button
                                icon
                                color={index2 === 0 ? "green" : "red"}
                                onClick={() =>
                                  index2 === 0
                                    ? this.addFormNotificationHourRange(index)
                                    : this.removeFormNotificationHourRange(index, index2)
                                }
                              >
                                <Icon name={index2 === 0 ? "plus" : "minus"} />
                              </Button>
                            </Grid.Column>
                          </Grid.Row>
                        ))}
                    </Grid>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Card.Content>
          </Card>
        ))}
        <h4>{t("clientPanelMisc:contactInformation")}</h4>
        <Form.Checkbox
          label={t("clientPanelMisc:useDefaultContactInformation")}
          checked={contactInformationUseDefault}
          onChange={(event, data) => setProperty("contactInformationUseDefault", data.checked)}
        />

        {this.getValidationError(`uiMeet3dSettings.contactInformation`) && (
          <Message error visible content={this.getValidationError(`uiMeet3dSettings.contactInformation`)} />
        )}

        {contactInformationUseDefault ? (
          <Form.Field>
            <label>{t("email")}</label>
            {email}
          </Form.Field>
        ) : (
          <Form.Field
            control={Input}
            name="contactInformationEmail"
            label={t("email")}
            value={contactInformationEmail}
            onBlur={onBlur}
            onChange={e => setProperty("contactInformationEmail", e.target.value)}
            error={getAggregatedError("uiMeet3dSettings.contactInformation.email", validation, validationErrors)}
          />
        )}

        {contactInformationUseDefault ? (
          <Form.Field>
            <label>{t("phone")}</label>
            {contactPhone}
          </Form.Field>
        ) : (
          <Form.Field
            control={Input}
            name="contactInformationContactPhone"
            label={t("phone")}
            value={contactInformationContactPhone}
            onBlur={onBlur}
            onChange={e => setProperty("contactInformationContactPhone", e.target.value)}
            error={getAggregatedError("uiMeet3dSettings.contactInformationContact.phone", validation, validationErrors)}
          />
        )}

        <Form.Field
          control={Input}
          name="contactInformationWebhook"
          label={t("clientPanelMisc:webhook")}
          value={contactInformationWebhook}
          onBlur={onBlur}
          onChange={e => setProperty("contactInformationWebhook", e.target.value)}
          error={getAggregatedError("uiMeet3dSettings.contactInformation.webhook", validation, validationErrors)}
        />
      </Form>
    );
  }
}

UiMeet3dNotificationForm.propTypes = {
  defaultTimezone: PropTypes.string.isRequired,
  contactPhone: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  formStore: PropTypes.instanceOf(FormStore).isRequired,
  t: PropTypes.func.isRequired,
  validationErrors: PropTypes.object.isRequired,
  encodeNotificationHourRanges: PropTypes.func.isRequired,
  decodeNotificationHourRanges: PropTypes.func.isRequired,
};

export default withTranslation()(UiMeet3dNotificationForm);
